// A combinatorial catalog of pixel effects.
//
// Each Recipe declares orthogonal parameter dimensions. The total number of
// unique variants is the cross-product of every recipe's dimensions, summed
// across recipes. A deterministic Fisher-Yates permutation produces an order
// that visits every variant exactly once — so "Next" can be hit thousands of
// times without ever repeating an effect within a session.

import type { Layer, LayerKind } from '@/core/types';
import { paletteIds, getPalette } from '@/filters/palette/lospec';
import { mulberry32 } from './rng';

const PALETTES = paletteIds(); // 20 currently bundled

export type Recipe = {
  id: string;
  name: string;
  /** Display labels per dimension — used to show what changes between variants. */
  dimNames: string[];
  /** Sizes of each dimension in row-major order. */
  dimensions: number[];
  /** Build a Layer[] given chosen indices into each dimension and a seed. */
  build: (vals: number[], seed: number) => Layer[];
};

// ---- Helpers --------------------------------------------------------------

let layerCounter = 0;
function lid(kind: string): string {
  layerCounter++;
  return `${kind[0]}${layerCounter.toString(36)}_${Date.now().toString(36).slice(-3)}`;
}

function layer(
  kind: LayerKind,
  params: Record<string, unknown>,
  opts: Partial<Pick<Layer, 'blend' | 'opacity' | 'enabled' | 'seed'>> = {},
): Layer {
  return {
    id: lid(String(kind)),
    kind,
    params,
    blend: opts.blend ?? 'source-over',
    opacity: opts.opacity ?? 1,
    enabled: opts.enabled ?? true,
    seed: opts.seed ?? 0,
  };
}

// Lookup tables — kept compact so we can scan them.
const SCALES   = [16, 24, 32, 48, 64, 96];                       // 6
const OCTAVES  = [2, 3, 4, 5, 6];                                 // 5
const CONTRAST = [0.7, 1.0, 1.3, 1.7, 2.2];                       // 5
const VOR_SITES   = [4, 8, 16, 32, 64];                           // 5
const VOR_HUE     = [0, 60, 120, 180, 240, 300];                  // 6
const DITHER_ALGO = ['floyd', 'atkinson', 'bayer4', 'bayer8'] as const; // 4
const LAT_SHAPE   = ['square', 'checker', 'hex', 'triangle', 'lines'] as const; // 5
const LAT_SIZE    = [4, 8, 12, 18, 28];                           // 5
const GR_TYPE     = ['linear', 'radial', 'conic'] as const;        // 3
const POST_LEVELS = [3, 4, 6, 8];                                 // 4
const SYM_MODES   = ['mirror-h', 'mirror-both', 'radial-4', 'radial-6', 'kaleido'] as const; // 5
const HALF_CELL   = [3, 5, 8, 12];                                // 4
const WORLEY_MODE = ['f1', 'f2-f1', 'f2'] as const;               // 3
const WORLEY_SITES = [12, 24, 48];                                // 3
const CELL_DENS   = [0.22, 0.32, 0.42, 0.52];                     // 4

function paletteParams(idx: number): Record<string, unknown> {
  const id = PALETTES[idx % PALETTES.length] ?? 'sweetie-16';
  return { palette: id };
}

function pickHexPair(seed: number): { a: string; b: string } {
  // Deterministically pick two contrasting colours from a random palette.
  const rng = mulberry32(seed);
  const palId = PALETTES[Math.floor(rng() * PALETTES.length)] ?? 'sweetie-16';
  const def = getPalette(palId);
  if (!def || def.colors.length < 2) return { a: '#000000', b: '#ffffff' };
  const i = Math.floor(rng() * def.colors.length);
  let j = Math.floor(rng() * def.colors.length);
  if (j === i) j = (i + 1) % def.colors.length;
  return { a: def.colors[i] as string, b: def.colors[j] as string };
}

// ---- Recipes --------------------------------------------------------------

export const RECIPES: Recipe[] = [
  // 1. Perlin → Palette  (6 × 5 × 5 × 20 = 3000 — but we cap with smaller dims)
  {
    id: 'perlin-pal',
    name: 'Perlin · Palette',
    dimNames: ['Scale', 'Octaves', 'Contrast', 'Palette'],
    dimensions: [SCALES.length, OCTAVES.length, CONTRAST.length, PALETTES.length],
    build: ([s, o, c, p], seed) => {
      const pair = pickHexPair(seed);
      return [
        layer('perlin', {
          scale: SCALES[s!]!, octaves: OCTAVES[o!]!,
          persistence: 0.5, contrast: CONTRAST[c!]!,
          a: pair.a, b: pair.b,
        }, { seed }),
        layer('palette', paletteParams(p!)),
      ];
    },
  },

  // 2. Perlin → Dither → Palette  (3 × 4 × 20 = 240)
  {
    id: 'perlin-dither-pal',
    name: 'Perlin · Dither · Palette',
    dimNames: ['Octaves', 'Algo', 'Palette'],
    dimensions: [3, DITHER_ALGO.length, PALETTES.length],
    build: ([o, d, p], seed) => {
      const oct = [3, 4, 5][o!]!;
      return [
        layer('perlin', {
          scale: 32, octaves: oct, persistence: 0.55, contrast: 1.4,
          a: '#000000', b: '#ffffff',
        }, { seed }),
        layer('dither', { algo: DITHER_ALGO[d!], levels: 4, amount: 1 }),
        layer('palette', paletteParams(p!)),
      ];
    },
  },

  // 3. Voronoi → Palette  (5 × 6 × 20 = 600)
  {
    id: 'voronoi-pal',
    name: 'Voronoi · Palette',
    dimNames: ['Sites', 'Hue', 'Palette'],
    dimensions: [VOR_SITES.length, VOR_HUE.length, PALETTES.length],
    build: ([s, h, p], seed) => [
      layer('voronoi', {
        sites: VOR_SITES[s!]!, hue: VOR_HUE[h!]!, spread: 70, edges: false,
        edgeColor: '#0a0a0a',
      }, { seed }),
      layer('palette', paletteParams(p!)),
    ],
  },

  // 4. Voronoi → Halftone → Palette  (3 × 4 × 20 = 240)
  {
    id: 'voronoi-half-pal',
    name: 'Voronoi · Halftone · Palette',
    dimNames: ['Sites', 'Cell', 'Palette'],
    dimensions: [3, HALF_CELL.length, PALETTES.length],
    build: ([s, c, p], seed) => {
      const sites = [8, 16, 32][s!]!;
      const pair = pickHexPair(seed);
      return [
        layer('voronoi', {
          sites, hue: 200, spread: 60, edges: false, edgeColor: '#0a0a0a',
        }, { seed }),
        layer('halftone', {
          cell: HALF_CELL[c!]!, ink: pair.a, paper: pair.b, shape: 'dot', invert: false,
        }),
        layer('palette', paletteParams(p!)),
      ];
    },
  },

  // 5. Worley → Palette  (3 × 3 × 20 = 180)
  {
    id: 'worley-pal',
    name: 'Worley · Palette',
    dimNames: ['Mode', 'Sites', 'Palette'],
    dimensions: [WORLEY_MODE.length, WORLEY_SITES.length, PALETTES.length],
    build: ([m, s, p], seed) => [
      layer('worley', {
        mode: WORLEY_MODE[m!], sites: WORLEY_SITES[s!]!, invert: false,
        a: '#000000', b: '#ffffff',
      }, { seed }),
      layer('palette', paletteParams(p!)),
    ],
  },

  // 6. Lattice → Palette  (5 × 5 × 20 = 500)
  {
    id: 'lattice-pal',
    name: 'Lattice · Palette',
    dimNames: ['Shape', 'Size', 'Palette'],
    dimensions: [LAT_SHAPE.length, LAT_SIZE.length, PALETTES.length],
    build: ([sh, sz, p], seed) => {
      const pair = pickHexPair(seed);
      return [
        layer('lattice', {
          shape: LAT_SHAPE[sh!], size: LAT_SIZE[sz!]!, thickness: 1,
          a: pair.a, b: pair.b,
        }),
        layer('palette', paletteParams(p!)),
      ];
    },
  },

  // 7. Gradient → Posterize → Palette  (3 × 4 × 20 = 240)
  {
    id: 'gradient-post-pal',
    name: 'Gradient · Posterize · Palette',
    dimNames: ['Type', 'Levels', 'Palette'],
    dimensions: [GR_TYPE.length, POST_LEVELS.length, PALETTES.length],
    build: ([t, l, p], seed) => {
      const pair = pickHexPair(seed);
      return [
        layer('gradient', {
          type: GR_TYPE[t!], angle: (seed * 13) % 360,
          a: pair.a, b: pair.b, power: 1.2,
        }),
        layer('posterize', { levels: POST_LEVELS[l!]! }),
        layer('palette', paletteParams(p!)),
      ];
    },
  },

  // 8. Cellular → Palette  (4 × 20 = 80)
  {
    id: 'cellular-pal',
    name: 'Game of Life · Palette',
    dimNames: ['Density', 'Palette'],
    dimensions: [CELL_DENS.length, PALETTES.length],
    build: ([d, p], seed) => {
      const pair = pickHexPair(seed);
      return [
        layer('cellular', { density: CELL_DENS[d!]!, alive: pair.b, dead: pair.a }, { seed }),
        layer('palette', paletteParams(p!)),
      ];
    },
  },

  // 9. Perlin → Symmetry → Palette  (5 × 20 = 100)
  {
    id: 'perlin-sym-pal',
    name: 'Perlin · Symmetry · Palette',
    dimNames: ['Symmetry', 'Palette'],
    dimensions: [SYM_MODES.length, PALETTES.length],
    build: ([m, p], seed) => {
      const pair = pickHexPair(seed);
      return [
        layer('perlin', {
          scale: 36, octaves: 4, persistence: 0.55, contrast: 1.5,
          a: pair.a, b: pair.b,
        }, { seed }),
        layer('symmetry', { mode: SYM_MODES[m!], source: 'left' }),
        layer('palette', paletteParams(p!)),
      ];
    },
  },

  // 10. Worley → Threshold → Palette  (3 × 5 × 20 = 300)
  {
    id: 'worley-thresh-pal',
    name: 'Worley · Threshold · Palette',
    dimNames: ['Mode', 'Threshold', 'Palette'],
    dimensions: [WORLEY_MODE.length, 5, PALETTES.length],
    build: ([m, t, p], seed) => {
      const pair = pickHexPair(seed);
      const thresholds = [0.25, 0.4, 0.5, 0.6, 0.75];
      return [
        layer('worley', {
          mode: WORLEY_MODE[m!], sites: 24, invert: false,
          a: '#000000', b: '#ffffff',
        }, { seed }),
        layer('threshold', {
          threshold: thresholds[t!]!, low: pair.a, high: pair.b,
        }),
        layer('palette', paletteParams(p!)),
      ];
    },
  },
];

// ---- Indexing -------------------------------------------------------------

const RECIPE_OFFSETS: number[] = [];
let TOTAL_VARIANTS = 0;
{
  let acc = 0;
  for (const r of RECIPES) {
    RECIPE_OFFSETS.push(acc);
    acc += r.dimensions.reduce((a, b) => a * b, 1);
  }
  TOTAL_VARIANTS = acc;
}

export function totalEffects(): number {
  return TOTAL_VARIANTS;
}

function decodeMixed(n: number, dims: number[]): number[] {
  const out: number[] = new Array(dims.length).fill(0);
  for (let i = dims.length - 1; i >= 0; i--) {
    const d = dims[i] as number;
    out[i] = n % d;
    n = Math.floor(n / d);
  }
  return out;
}

export type DecodedEffect = {
  recipe: Recipe;
  vals: number[];
  globalIndex: number;
  /** Stable seed derived from globalIndex so the same index is visually reproducible. */
  seed: number;
};

export function decodeEffect(globalIndex: number): DecodedEffect {
  if (globalIndex < 0 || globalIndex >= TOTAL_VARIANTS) {
    throw new Error(`effect index out of range: ${globalIndex}`);
  }
  // Find which recipe this index falls into.
  let recipeIdx = 0;
  for (let i = 0; i < RECIPES.length; i++) {
    const off = RECIPE_OFFSETS[i] as number;
    const next = i + 1 < RECIPES.length ? (RECIPE_OFFSETS[i + 1] as number) : TOTAL_VARIANTS;
    if (globalIndex < next) {
      recipeIdx = i;
      break;
    }
    void off;
  }
  const recipe = RECIPES[recipeIdx] as Recipe;
  const local = globalIndex - (RECIPE_OFFSETS[recipeIdx] as number);
  const vals = decodeMixed(local, recipe.dimensions);
  // Deterministic seed: a hash of the global index keeps each variant visually
  // reproducible while differing across the catalog.
  const seed = ((globalIndex * 2654435761) >>> 0) || 1;
  return { recipe, vals, globalIndex, seed };
}

export function buildEffect(globalIndex: number): { layers: Layer[]; meta: DecodedEffect } {
  const dec = decodeEffect(globalIndex);
  const layers = dec.recipe.build(dec.vals, dec.seed);
  return { layers, meta: dec };
}

export function effectLabel(meta: DecodedEffect): { name: string; tags: string } {
  const r = meta.recipe;
  // Build a short tag string showing each dimension's chosen value name.
  const tagParts: string[] = [];
  for (let i = 0; i < r.dimensions.length; i++) {
    tagParts.push(`${r.dimNames[i]}=${describeValue(r, i, meta.vals[i] as number)}`);
  }
  return { name: r.name, tags: tagParts.join(' · ') };
}

function describeValue(r: Recipe, dim: number, idx: number): string {
  // Show palette names directly; numeric dims show their value if known.
  const dn = r.dimNames[dim];
  if (dn === 'Palette') return PALETTES[idx] ?? String(idx);
  // Best effort: look up common dimension names against known tables
  const known: Record<string, readonly (string | number)[]> = {
    Scale: SCALES,
    Octaves: OCTAVES,
    Contrast: CONTRAST,
    Sites: r.id === 'voronoi-pal' ? VOR_SITES : (r.id === 'voronoi-half-pal' ? [8, 16, 32] : WORLEY_SITES),
    Hue: VOR_HUE,
    Algo: DITHER_ALGO,
    Shape: LAT_SHAPE,
    Size: LAT_SIZE,
    Type: GR_TYPE,
    Levels: POST_LEVELS,
    Mode: r.id.startsWith('worley') ? WORLEY_MODE : SYM_MODES,
    Symmetry: SYM_MODES,
    Cell: HALF_CELL,
    Density: CELL_DENS,
    Threshold: [0.25, 0.4, 0.5, 0.6, 0.75],
  };
  const table = dn ? known[dn] : undefined;
  if (table && idx < table.length) return String(table[idx]);
  return String(idx);
}

// ---- Permutation stream ---------------------------------------------------
//
// Produces a no-repeat order over all effect indices using Fisher-Yates with a
// session seed. Lazy-built per session; reused by the browser modal.

let cachedOrder: number[] | null = null;
let cachedSeed = 0;

export function getStream(seed = (Date.now() & 0xffffffff)): EffectStream {
  if (cachedOrder === null || cachedSeed !== seed) {
    cachedOrder = buildPermutation(TOTAL_VARIANTS, seed);
    cachedSeed = seed;
  }
  return new EffectStream(cachedOrder);
}

function buildPermutation(n: number, seed: number): number[] {
  const out = new Array<number>(n);
  for (let i = 0; i < n; i++) out[i] = i;
  const rng = mulberry32(seed);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = out[i] as number;
    out[i] = out[j] as number;
    out[j] = tmp;
  }
  return out;
}

export class EffectStream {
  private cursor = 0;
  constructor(private readonly order: number[]) {}

  size(): number { return this.order.length; }
  cursorIndex(): number { return this.cursor; }

  current(): number { return this.order[this.cursor] as number; }

  next(): number {
    this.cursor = (this.cursor + 1) % this.order.length;
    return this.current();
  }

  prev(): number {
    this.cursor = (this.cursor - 1 + this.order.length) % this.order.length;
    return this.current();
  }

  jumpRandom(): number {
    this.cursor = Math.floor(Math.random() * this.order.length);
    return this.current();
  }

  setCursor(i: number): void {
    this.cursor = ((i % this.order.length) + this.order.length) % this.order.length;
  }
}
